/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react';
import { Space, Tooltip, Typography } from 'antd';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import type { RotationHistoryEntry, RotationHistoryFamily } from '../../api/fieldsApi';

const { Text } = Typography;

const LIFECYCLE_STATUSES = new Set(['dead', 'transferred', 'sold', 'archived']);

const styles = {
  bar: css({
    display: 'flex',
    width: '100%',
    height: 32,
    borderRadius: 6,
    overflow: 'hidden',
    border: '1px solid #e8e8e8',
    // Gray background represents empty (fallow) periods
    backgroundColor: '#ebebeb',
  }),
  segment: css({
    height: '100%',
    transition: 'opacity 0.2s',
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
    },
  }),
  segmentExcluded: css({
    height: '100%',
    cursor: 'pointer',
    opacity: 0.45,
    backgroundImage:
      'repeating-linear-gradient(-45deg, transparent, transparent 3px, rgba(255,255,255,0.5) 3px, rgba(255,255,255,0.5) 6px)',
    '&:hover': {
      opacity: 0.6,
    },
  }),
  legend: css({
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px 16px',
    marginTop: 8,
  }),
  legendItem: css({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
  }),
  legendDot: css({
    width: 10,
    height: 10,
    borderRadius: 3,
    flexShrink: 0,
  }),
  emptyBar: css({
    display: 'flex',
    width: '100%',
    height: 32,
    borderRadius: 6,
    border: '1px dashed #d9d9d9',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  timelineLabels: css({
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 4,
    fontSize: 11,
    color: '#999',
  }),
};

interface RotationHistoryBarProps {
  entries: RotationHistoryEntry[];
  families: RotationHistoryFamily[];
  /** In simulation mode, use this date as "now" instead of Date.now() */
  simDate?: string;
}

const RotationHistoryBar: React.FC<RotationHistoryBarProps> = ({ entries, families, simDate }) => {
  const { t } = useTranslation('planning');
  const { segments, startYear, endYear } = useMemo(() => {
    if (!entries || entries.length === 0) {
      return { segments: [], startYear: null, endYear: null };
    }

    const effectiveNow = simDate ? new Date(simDate).getTime() : Date.now();

    // Find timeline bounds
    const dates = entries.flatMap((e) => [
      e.fromDate ? new Date(e.fromDate).getTime() : null,
      e.toDate ? new Date(e.toDate).getTime() : null,
    ]).filter((d): d is number => d !== null);

    if (dates.length === 0) {
      return { segments: [], startYear: null, endYear: null };
    }

    const minTime = Math.min(...dates);
    const maxTime = Math.max(...dates, effectiveNow);
    const totalSpan = maxTime - minTime || 1;

    const segs = entries
      .filter((e) => e.fromDate)
      .map((e) => {
        const from = new Date(e.fromDate!).getTime();
        const to = e.toDate ? new Date(e.toDate).getTime() : effectiveNow;
        const left = ((from - minTime) / totalSpan) * 100;
        const width = Math.max(((to - from) / totalSpan) * 100, 0.5); // min 0.5% visible
        return {
          ...e,
          left,
          width,
        };
      });

    return {
      segments: segs,
      startYear: new Date(minTime).getFullYear(),
      endYear: new Date(maxTime).getFullYear(),
    };
  }, [entries, simDate]);

  if (!entries || entries.length === 0) {
    return (
      <div css={styles.emptyBar}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t('rotation.empty')}
        </Text>
      </div>
    );
  }

  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      <div css={styles.bar} style={{ position: 'relative' }}>
        {segments.map((seg, i) => (
          <Tooltip
            key={i}
            title={
              <div>
                <div><strong>{seg.cropName}</strong></div>
                <div>{seg.familyRu}</div>
                <div style={{ fontSize: 11 }}>
                  {seg.fromDate} &rarr; {seg.toDate ?? t('rotation.now')}
                  {seg.durationDays != null && ` (${t('rotation.days', { count: seg.durationDays })})`}
                </div>
                {seg.isExcluded && seg.lifecycleStatus && (
                  <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>
                    ⚠ {t('rotation.excluded', {
                      status: LIFECYCLE_STATUSES.has(seg.lifecycleStatus)
                        ? t(`rotation.lifecycle.${seg.lifecycleStatus}`)
                        : seg.lifecycleStatus,
                    })}
                  </div>
                )}
              </div>
            }
          >
            <div
              css={seg.isExcluded ? styles.segmentExcluded : styles.segment}
              style={{
                position: 'absolute',
                left: `${seg.left}%`,
                width: `${seg.width}%`,
                backgroundColor: seg.familyColor,
              }}
            />
          </Tooltip>
        ))}
      </div>

      {startYear !== null && endYear !== null && (
        <div css={styles.timelineLabels}>
          <span>{startYear}</span>
          {endYear !== startYear && <span>{endYear}</span>}
        </div>
      )}

      <div css={styles.legend}>
        {families.map((f) => (
          <div key={f.familyLatin} css={styles.legendItem}>
            <div
              css={styles.legendDot}
              style={{ backgroundColor: f.color }}
            />
            <span>{t('rotation.familyLegend', { family: f.familyRu, count: f.count, days: t('rotation.days', { count: f.totalDays }) })}</span>
          </div>
        ))}
      </div>
    </Space>
  );
};

export default RotationHistoryBar;
